import type {
  CatalogFacetDescriptor,
  CatalogSearchPayload,
} from "./catalogSearch";

/**
 * Small, product-free SSR hand-off for the first catalog page. Product cards
 * already belong to the Vuex product module; putting them here would serialize
 * the same records twice.
 */
export const CATALOG_HYDRATION_CONTRACT = "catalog-hydration/v1" as const;

const MAX_ROUTE_LENGTH = 2048;
const MAX_CATEGORY_ID_LENGTH = 128;
const MAX_FACETS = 32;
const MAX_FACET_VALUES = 512;
const MAX_TOTAL_FACET_VALUES = 2048;
const MAX_ALIASES = 8;
const MAX_APPLIED = 32;
const MAX_APPLIED_VALUES = 96;

export type CatalogHydrationMetadata = {
  version: typeof CATALOG_HYDRATION_CONTRACT;
  route: string;
  categoryId: string;
  page: number;
  pageSize: number;
  total: number;
  source: "orchestrator" | "legacy";
  facets: CatalogFacetDescriptor[];
  applied: NonNullable<CatalogSearchPayload["applied"]>;
  status: string;
  requestId: string;
  generationId: string;
  mappingRevision: string;
  indexLagSeconds: number | null;
};

export type CatalogHydrationMatch = Pick<
  CatalogHydrationMetadata,
  "route" | "categoryId" | "page" | "pageSize"
>;

const boundedText = (value: unknown, maximumLength: number) => {
  if (typeof value !== "string") return "";
  const normalized = value.replace(/[\u0000-\u001f\u007f]/g, "").trim();
  return normalized.length <= maximumLength ? normalized : "";
};

const boundedInteger = (value: unknown, minimum: number, maximum: number) =>
  typeof value === "number" &&
  Number.isSafeInteger(value) &&
  value >= minimum &&
  value <= maximum
    ? value
    : null;

const boundedNullableNumber = (
  value: unknown,
  minimum: number,
  maximum: number
) =>
  value === null || value === undefined
    ? null
    : typeof value === "number" &&
      Number.isFinite(value) &&
      value >= minimum &&
      value <= maximum
    ? value
    : null;

const FACET_KEY = /^[a-z][a-z0-9_]{0,63}$/;
const FACET_VALUE_KEY = /^[a-z0-9][a-z0-9_-]{0,127}$/i;

const normalizeFacet = (value: unknown): CatalogFacetDescriptor | null => {
  const source = value as Partial<CatalogFacetDescriptor> | null;
  const key = boundedText(source?.key, 64);
  const dataType = boundedText(source?.dataType, 64);
  const uiType = boundedText(source?.uiType, 64);
  if (!FACET_KEY.test(key) || !dataType || !uiType) return null;

  const sourceValues = source?.values;
  if (
    sourceValues != null &&
    (!Array.isArray(sourceValues) || sourceValues.length > MAX_FACET_VALUES)
  ) {
    return null;
  }
  const values = (sourceValues || []).map((candidate) => {
    const item = candidate || {};
    const valueKey = boundedText(item.key, 128);
    if (!FACET_VALUE_KEY.test(valueKey)) return null;
    const aliases = item.aliases;
    if (
      aliases != null &&
      (!Array.isArray(aliases) || aliases.length > MAX_ALIASES)
    ) {
      return null;
    }
    const normalizedAliases = (aliases || [])
      .map((alias) => boundedText(alias, 128))
      .filter((alias) => FACET_VALUE_KEY.test(alias));
    if (normalizedAliases.length !== (aliases || []).length) return null;
    const count = boundedInteger(item.count, 0, 10_000_000);
    if (
      count === null ||
      typeof item.selected !== "boolean" ||
      typeof item.disabled !== "boolean"
    ) {
      return null;
    }
    return {
      key: valueKey,
      ...(normalizedAliases.length ? { aliases: normalizedAliases } : {}),
      ...(boundedText(item.label, 256)
        ? { label: boundedText(item.label, 256) }
        : {}),
      count,
      selected: item.selected,
      disabled: item.disabled,
      ...(boundedText(item.swatch, 256)
        ? { swatch: boundedText(item.swatch, 256) }
        : {}),
      ...(boundedText(item.image, 1024)
        ? { image: boundedText(item.image, 1024) }
        : {}),
    };
  });
  if (values.some((item) => item === null)) return null;

  return {
    key,
    ...(boundedText(source?.label, 256)
      ? { label: boundedText(source?.label, 256) }
      : {}),
    ...(boundedText(source?.helpText, 512)
      ? { helpText: boundedText(source?.helpText, 512) }
      : {}),
    dataType,
    uiType,
    multiSelect: source?.multiSelect === true,
    ...(sourceValues != null
      ? { values: values as NonNullable<CatalogFacetDescriptor["values"]> }
      : {}),
    ...(boundedText(source?.availableMin, 64)
      ? { availableMin: boundedText(source?.availableMin, 64) }
      : {}),
    ...(boundedText(source?.availableMax, 64)
      ? { availableMax: boundedText(source?.availableMax, 64) }
      : {}),
    ...(boundedText(source?.selectedMin, 64)
      ? { selectedMin: boundedText(source?.selectedMin, 64) }
      : {}),
    ...(boundedText(source?.selectedMax, 64)
      ? { selectedMax: boundedText(source?.selectedMax, 64) }
      : {}),
    ...(boundedText(source?.step, 64)
      ? { step: boundedText(source?.step, 64) }
      : {}),
    ...(boundedText(source?.unit, 64)
      ? { unit: boundedText(source?.unit, 64) }
      : {}),
  };
};

const normalizeApplied = (
  value: unknown
): NonNullable<CatalogSearchPayload["applied"]> | null => {
  if (!Array.isArray(value) || value.length > MAX_APPLIED) return null;
  const applied = value.map((candidate) => {
    const item = candidate || {};
    const key = boundedText(item.key, 64);
    if (!FACET_KEY.test(key)) return null;
    const values = item.values;
    if (
      values != null &&
      (!Array.isArray(values) || values.length > MAX_APPLIED_VALUES)
    ) {
      return null;
    }
    const normalizedValues = (values || []).map((entry) =>
      boundedText(entry, 128)
    );
    if (normalizedValues.some((entry) => !FACET_VALUE_KEY.test(entry))) {
      return null;
    }
    return {
      key,
      ...(values != null ? { values: normalizedValues } : {}),
      ...(boundedText(item.min, 64) ? { min: boundedText(item.min, 64) } : {}),
      ...(boundedText(item.max, 64) ? { max: boundedText(item.max, 64) } : {}),
    };
  });
  return applied.some((entry) => entry === null)
    ? null
    : (applied as NonNullable<CatalogSearchPayload["applied"]>);
};

/** Pure, bounded validation of the Vuex SSR envelope. */
export function normalizeCatalogHydration(
  value: unknown
): CatalogHydrationMetadata | null {
  const source = value as Partial<CatalogHydrationMetadata> | null;
  if (source?.version !== CATALOG_HYDRATION_CONTRACT) return null;
  const route = boundedText(source.route, MAX_ROUTE_LENGTH);
  const categoryId = boundedText(source.categoryId, MAX_CATEGORY_ID_LENGTH);
  const page = boundedInteger(source.page, 1, 100_000);
  const pageSize = boundedInteger(source.pageSize, 1, 200);
  const total = boundedInteger(source.total, 0, 10_000_000);
  const sourceName = source.source;
  const facets =
    Array.isArray(source.facets) && source.facets.length <= MAX_FACETS
      ? source.facets.map(normalizeFacet)
      : null;
  const applied = normalizeApplied(source.applied);
  const indexLagSeconds = boundedNullableNumber(
    source.indexLagSeconds,
    0,
    31_536_000
  );
  if (
    !route.startsWith("/") ||
    !categoryId ||
    page === null ||
    pageSize === null ||
    total === null ||
    (sourceName !== "orchestrator" && sourceName !== "legacy") ||
    !facets ||
    facets.some((facet) => facet === null) ||
    facets.reduce((count, facet) => count + (facet?.values?.length || 0), 0) >
      MAX_TOTAL_FACET_VALUES ||
    !applied ||
    (source.indexLagSeconds !== null &&
      source.indexLagSeconds !== undefined &&
      indexLagSeconds === null)
  ) {
    return null;
  }
  return {
    version: CATALOG_HYDRATION_CONTRACT,
    route,
    categoryId,
    page,
    pageSize,
    total,
    source: sourceName,
    facets: facets as CatalogFacetDescriptor[],
    applied,
    status: boundedText(source.status, 64) || "OK",
    requestId: boundedText(source.requestId, 128),
    generationId: boundedText(source.generationId, 128),
    mappingRevision: boundedText(source.mappingRevision, 128),
    indexLagSeconds,
  };
}

export function matchesCatalogHydration(
  value: unknown,
  exact: CatalogHydrationMatch
): CatalogHydrationMetadata | null {
  const metadata = normalizeCatalogHydration(value);
  return metadata &&
    metadata.route === exact.route &&
    metadata.categoryId === exact.categoryId &&
    metadata.page === exact.page &&
    metadata.pageSize === exact.pageSize
    ? metadata
    : null;
}
